import { useCallback, useEffect, useRef, useState, type PointerEvent, type RefObject } from 'react';

/**
 * Direct manipulation on a stage, measured once and painted once per frame.
 *
 * Pointer events with `setPointerCapture`, exactly as the slide layer editor
 * does it, so a drag survives the pointer leaving the layer and works the same
 * under a finger. The stage is measured once when the pointer goes down, every
 * pointermove after that is arithmetic against the cached box, and the result
 * is written straight to the element on a `requestAnimationFrame` — one write
 * per frame, no layout read, and no React state until the gesture ends. The
 * map only changes on pointerup, which is the one thing the screen around it
 * has to re-render for.
 *
 * One pointer at a time: a second finger (a non-primary pointer, or any other
 * pointer while one is held) neither starts a gesture nor moves the one in
 * hand. And a gesture only starts moving once the pointer has travelled
 * `DRAG_THRESHOLD` px, so a tap that wobbles selects a layer without
 * nudging it.
 */

/** Client px a pointer has to travel before a press becomes a drag. */
const DRAG_THRESHOLD = 4;

/** Where the pointer is on the stage, in fractions of it. */
export interface StagePoint {
  fx: number;
  fy: number;
}

/** The stage's client box, measured once per gesture. */
export interface StageBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** One in-flight pointer gesture on the stage. */
interface Gesture {
  /** Where the pointer capture sits — the grabbed element itself. */
  capture: Element;
  /** What the frames paint. For a resize handle that is the zone, not the grip. */
  element: HTMLElement;
  pointerId: number;
  stage: StageBox;
  /** Where the pointer went down, in client px, for the drag threshold. */
  origin: { x: number; y: number };
  /** Past the threshold: from here on every move counts. */
  moving: boolean;
  /** Undoes the imperative styles and commits the value under them. */
  finish: (element: HTMLElement) => void;
  /** The latest queued write; only the last one before a frame runs. */
  draw: ((element: HTMLElement, stage: StageBox) => void) | null;
  frame: number | null;
}

export interface StageGesture {
  /** True between pointerdown and pointerup. The only state a gesture sets. */
  isActive: boolean;
  /**
   * Whether this pointer may start a gesture: a primary pointer, with none in
   * hand. A press that fails it should change nothing, selection included.
   */
  canBegin: (event: PointerEvent<HTMLElement>) => boolean;
  /**
   * Starts one: measures the stage, captures the pointer, and answers where the
   * pointer is in fractions of the stage. Null when the stage is unmeasurable.
   */
  begin: (
    event: PointerEvent<HTMLElement>,
    element: HTMLElement,
    finish: (element: HTMLElement) => void,
  ) => StagePoint | null;
  /**
   * Where the pointer is now, from the cached box. Touches no layout. Null for
   * another pointer, and until the gesture's pointer is past the threshold.
   */
  track: (event: PointerEvent<HTMLElement>) => StagePoint | null;
  /** Queues one write for the next frame; the latest call wins. */
  paint: (draw: (element: HTMLElement, stage: StageBox) => void) => void;
  /**
   * Ends it and runs the `finish` the gesture began with. Given an event, only
   * the gesture's own pointer ends it.
   */
  end: (event?: PointerEvent<HTMLElement>) => void;
}

/**
 * The measure-once, paint-on-a-frame machinery behind every gesture in the
 * design builder — moving a layer, resizing it, moving the circle, sizing it.
 */
export function useStageGesture(stageRef: RefObject<HTMLDivElement | null>): StageGesture {
  const gestureRef = useRef<Gesture | null>(null);
  const [isActive, setIsActive] = useState(false);

  const canBegin = useCallback(
    (event: PointerEvent<HTMLElement>) => event.isPrimary && gestureRef.current === null,
    [],
  );

  const begin = useCallback(
    (
      event: PointerEvent<HTMLElement>,
      element: HTMLElement,
      finish: (element: HTMLElement) => void,
    ): StagePoint | null => {
      const stage = stageRef.current;
      if (!stage || !canBegin(event)) return null;
      // The one layout read of the gesture. The stage cannot resize under a
      // held pointer; a window resize ends the gesture rather than re-measuring.
      const rect = stage.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return null;

      const box: StageBox = {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      };
      gestureRef.current = {
        capture: event.currentTarget,
        element,
        pointerId: event.pointerId,
        stage: box,
        origin: { x: event.clientX, y: event.clientY },
        moving: false,
        finish,
        draw: null,
        frame: null,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
      setIsActive(true);
      return {
        fx: (event.clientX - box.left) / box.width,
        fy: (event.clientY - box.top) / box.height,
      };
    },
    [stageRef, canBegin],
  );

  const track = useCallback((event: PointerEvent<HTMLElement>): StagePoint | null => {
    const gesture = gestureRef.current;
    if (gesture === null || event.pointerId !== gesture.pointerId) return null;
    if (!gesture.moving) {
      const travel = Math.hypot(event.clientX - gesture.origin.x, event.clientY - gesture.origin.y);
      if (travel < DRAG_THRESHOLD) return null;
      gesture.moving = true;
    }
    const { stage } = gesture;
    return {
      fx: (event.clientX - stage.left) / stage.width,
      fy: (event.clientY - stage.top) / stage.height,
    };
  }, []);

  const paint = useCallback((draw: (element: HTMLElement, stage: StageBox) => void) => {
    const gesture = gestureRef.current;
    if (gesture === null) return;
    gesture.draw = draw;
    if (gesture.frame !== null) return;
    gesture.frame = requestAnimationFrame(() => {
      const current = gestureRef.current;
      if (current === null) return;
      current.frame = null;
      current.draw?.(current.element, current.stage);
    });
  }, []);

  const end = useCallback((event?: PointerEvent<HTMLElement>) => {
    const gesture = gestureRef.current;
    if (gesture === null) return;
    if (event && event.pointerId !== gesture.pointerId) return;
    gestureRef.current = null;
    if (gesture.frame !== null) cancelAnimationFrame(gesture.frame);
    if (gesture.capture.hasPointerCapture(gesture.pointerId)) {
      gesture.capture.releasePointerCapture(gesture.pointerId);
    }
    setIsActive(false);
    gesture.finish(gesture.element);
  }, []);

  /**
   * A resize invalidates the box this gesture is standing on, so it ends here.
   * It ends rather than reverts: the last painted position is one the author
   * watched land.
   */
  useEffect(() => {
    if (!isActive) return;

    function handleResize() {
      end();
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [end, isActive]);

  /** An unmount mid-gesture drops the frame; it commits nothing. */
  useEffect(
    () => () => {
      const gesture = gestureRef.current;
      if (gesture !== null && gesture.frame !== null) cancelAnimationFrame(gesture.frame);
      gestureRef.current = null;
    },
    [],
  );

  return { isActive, canBegin, begin, track, paint, end };
}
